import { useState, useEffect } from 'react'
import { useAppLockStore } from '@/stores/app-lock.store'
import { useAuthStore } from '@/stores/auth.store'
import { appLockService } from '@/lib/app-lock'
import { Logo } from '@/components/common/Logo'

const MAX_FAILED_ATTEMPTS = 5

export function LockScreen() {
  const [pin, setPin] = useState('')
  const [error, setError] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)

  const {
    unlock,
    biometricEnabled,
    biometricAvailable,
    biometryType,
    failedAttempts,
    incrementFailedAttempts,
    resetFailedAttempts,
  } = useAppLockStore()

  const logout = useAuthStore((state) => state.logout)

  // Auto-attempt biometric on mount
  useEffect(() => {
    if (biometricEnabled && biometricAvailable) {
      handleBiometric()
    }
  }, [])

  // Keyboard support for typing PIN
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isVerifying) return

      if (e.key >= '0' && e.key <= '9') {
        handlePinInput(e.key)
      } else if (e.key === 'Backspace') {
        handleBackspace()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [pin, isVerifying])

  // Check for max attempts
  useEffect(() => {
    if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
      handleForceLogout()
    }
  }, [failedAttempts])

  const handleBiometric = async () => {
    const success = await appLockService.authenticateWithBiometric()
    if (success) {
      resetFailedAttempts()
      unlock()
    }
  }

  const handlePinInput = (digit: string) => {
    if (pin.length < 4) {
      const newPin = pin + digit
      setPin(newPin)
      setError('')

      if (newPin.length === 4) {
        verifyPin(newPin)
      }
    }
  }

  const handleBackspace = () => {
    setPin(pin.slice(0, -1))
    setError('')
  }

  const verifyPin = async (enteredPin: string) => {
    setIsVerifying(true)
    try {
      const isValid = await appLockService.verifyPIN(enteredPin)
      if (isValid) {
        resetFailedAttempts()
        unlock()
      } else {
        incrementFailedAttempts()
        setError(`Incorrect PIN. ${MAX_FAILED_ATTEMPTS - failedAttempts - 1} attempts remaining.`)
        setPin('')
      }
    } finally {
      setIsVerifying(false)
    }
  }

  const handleForceLogout = async () => {
    await appLockService.clearAll()
    await logout()
    window.location.reload()
  }

  const biometryName = appLockService.getBiometryTypeName(biometryType)

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white pt-safe pb-safe">
      <div className="flex flex-col items-center w-full max-w-xs px-6">
        {/* Logo */}
        <div className="mb-8">
          <Logo size="lg" />
        </div>

        {/* Title */}
        <h1 className="text-xl font-semibold text-gray-900 mb-2">
          Enter PIN to unlock
        </h1>

        {/* Error message */}
        {error && (
          <p className="text-sm text-red-600 mb-4 text-center">{error}</p>
        )}

        {/* PIN dots */}
        <div className="flex gap-4 mb-8">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full border-2 ${
                i < pin.length
                  ? 'bg-primary-600 border-primary-600'
                  : 'border-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Number pad */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <button
              key={num}
              type="button"
              onClick={() => handlePinInput(num.toString())}
              disabled={isVerifying}
              className="w-16 h-16 rounded-full bg-gray-100 text-2xl font-semibold text-gray-900 active:bg-gray-200 disabled:opacity-50"
            >
              {num}
            </button>
          ))}
          <div /> {/* Empty cell */}
          <button
            type="button"
            onClick={() => handlePinInput('0')}
            disabled={isVerifying}
            className="w-16 h-16 rounded-full bg-gray-100 text-2xl font-semibold text-gray-900 active:bg-gray-200 disabled:opacity-50"
          >
            0
          </button>
          <button
            type="button"
            onClick={handleBackspace}
            disabled={isVerifying || pin.length === 0}
            className="w-16 h-16 rounded-full bg-gray-100 text-xl text-gray-600 active:bg-gray-200 disabled:opacity-50"
          >
            <svg className="w-6 h-6 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414 6.414a2 2 0 001.414.586H19a2 2 0 002-2V7a2 2 0 00-2-2h-8.172a2 2 0 00-1.414.586L3 12z" />
            </svg>
          </button>
        </div>

        {/* Biometric button */}
        {biometricEnabled && biometricAvailable && (
          <button
            type="button"
            onClick={handleBiometric}
            className="flex items-center gap-2 text-primary-600 font-medium mb-4"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
            </svg>
            Use {biometryName}
          </button>
        )}

        {/* Logout button */}
        <button
          type="button"
          onClick={handleForceLogout}
          className="text-gray-500 text-sm"
        >
          Logout
        </button>
      </div>
    </div>
  )
}
